import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { classes, identifiantUnique } from '../lib/divers';

type TonNotification = 'neutre' | 'succes' | 'erreur';

interface Notification {
  id: string;
  ton: TonNotification;
  texte: string;
}

interface ApiNotifications {
  succes: (texte: string) => void;
  erreur: (texte: string) => void;
  info: (texte: string) => void;
}

const Contexte = createContext<ApiNotifications | null>(null);

const DUREE_MS = 6000;

/**
 * Notifications éphémères.
 *
 * Elles confirment une action réussie ; elles ne remplacent JAMAIS un message d'erreur en
 * place. Une erreur qui disparaît au bout de six secondes est une erreur perdue — les échecs
 * d'opération restent affichés dans le formulaire ou l'écran qui les a produits.
 */
export function FournisseurNotifications({ children }: { children: ReactNode }): JSX.Element {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const minuteurs = useRef(new Map<string, number>());

  const retirer = useCallback((id: string) => {
    setNotifications((liste) => liste.filter((n) => n.id !== id));
    const minuteur = minuteurs.current.get(id);
    if (minuteur !== undefined) {
      window.clearTimeout(minuteur);
      minuteurs.current.delete(id);
    }
  }, []);

  const ajouter = useCallback(
    (ton: TonNotification, texte: string) => {
      const id = identifiantUnique();
      setNotifications((liste) => [...liste, { id, ton, texte }]);
      const minuteur = window.setTimeout(() => {
        retirer(id);
      }, DUREE_MS);
      minuteurs.current.set(id, minuteur);
    },
    [retirer],
  );

  const api = useMemo<ApiNotifications>(
    () => ({
      succes: (texte: string) => {
        ajouter('succes', texte);
      },
      erreur: (texte: string) => {
        ajouter('erreur', texte);
      },
      info: (texte: string) => {
        ajouter('neutre', texte);
      },
    }),
    [ajouter],
  );

  return (
    <Contexte.Provider value={api}>
      {children}
      <div className="gv-toasts gv-sans-impression" aria-live="polite" aria-atomic="false">
        {notifications.map((n) => (
          <div
            key={n.id}
            className={classes('gv-toast', n.ton !== 'neutre' && `gv-toast--${n.ton}`)}
            role={n.ton === 'erreur' ? 'alert' : 'status'}
          >
            <span>{n.texte}</span>
            <button
              type="button"
              className="gv-toast__fermer"
              aria-label="Fermer la notification"
              onClick={() => {
                retirer(n.id);
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </Contexte.Provider>
  );
}

export function useNotifications(): ApiNotifications {
  const contexte = useContext(Contexte);
  if (!contexte) {
    throw new Error('useNotifications doit être utilisé sous <FournisseurNotifications>.');
  }
  return contexte;
}
